import clsx from 'clsx';
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  ReactNode,
  useState,
} from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';

import { Button } from '../Button';
import { Popover } from '../Popover';
import * as styles from './TooltipPopover.css';

export const TooltipPopover = ({
  triggerContent,
  children,
  onClosed,
  contentVariant,
  contentStyle,
  showArrow = true,
  ...rest
}: ComponentPropsWithoutRef<typeof Button> & {
  triggerContent: ReactNode;
  title?: string;
  onClosed?: () => void;
  contentVariant?: 'theme' | 'light' | 'dark' | 'darkGray' | 'v2';
  contentStyle?: CSSProperties;
  showArrow?: boolean;
}) => {
  const { isDarkMode } = useSiteTheme();

  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (!open) {
          onClosed?.();
        }
      }}
    >
      <Popover.Trigger asChild>
        <Button
          variant="link"
          shape="none"
          size="unset"
          {...rest}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {triggerContent}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        className={clsx(styles.popoverContentWrapper, {
          [styles.lightContentWrapper]: contentVariant === 'light',
          [styles.darkContentWrapper]: contentVariant === 'dark',
          [styles.darkGrayContentWrapper]: contentVariant === 'darkGray',
          [styles.popoverContentWrapperV2]: contentVariant === 'v2',
          [styles.popoverContentWrapperV2Dark]:
            contentVariant === 'v2' && isDarkMode,
        })}
        style={contentStyle}
        side="bottom"
        align="start"
        sideOffset={4}
        arrowPadding={8}
      >
        {children}
        {showArrow && (
          <Popover.Arrow
            className={clsx(styles.contentArrow, {
              [styles.lightContentArrow]: contentVariant === 'light',
              [styles.darkContentArrow]: contentVariant === 'dark',
              [styles.darkGrayContentArrow]: contentVariant === 'darkGray',
            })}
          />
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export const HoverPopover = ({
  triggerContent,
  children,
  onClosed,
  contentVariant,
  contentStyle,
  ...rest
}: ComponentPropsWithoutRef<typeof Button> & {
  triggerContent: ReactNode;
  title?: string;
  onClosed?: () => void;
  contentVariant?: 'theme' | 'light' | 'dark' | 'darkGray';
  contentStyle?: CSSProperties;
}) => {
  const [open, setOpen] = useState(false);

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        asChild
      >
        {triggerContent}
      </Popover.Trigger>
      <Popover.Content
        className={clsx(styles.popoverContentWrapper, {
          [styles.lightContentWrapper]: contentVariant === 'light',
          [styles.darkContentWrapper]: contentVariant === 'dark',
          [styles.darkGrayContentWrapper]: contentVariant === 'darkGray',
        })}
        style={contentStyle}
        side="bottom"
        align="start"
        sideOffset={4}
        arrowPadding={8}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        <Popover.Arrow
          className={clsx(styles.contentArrow, {
            [styles.lightContentArrow]: contentVariant === 'light',
            [styles.darkContentArrow]: contentVariant === 'dark',
            [styles.darkGrayContentArrow]: contentVariant === 'darkGray',
          })}
        />
      </Popover.Content>
    </Popover.Root>
  );
};
